import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";

const AuthContext = createContext(null);
const TOKEN_KEY = "leovera_user_token";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const userApi = axios.create({ baseURL: API });

// "Resta collegato" support: the token lives in localStorage (persists across
// browser restarts) when remember_me is true, or sessionStorage (cleared when
// the tab/browser closes) when false — same key in both stores so every other
// reader only has to check both, never two different key names.
const getStoredToken = () => sessionStorage.getItem(TOKEN_KEY) || localStorage.getItem(TOKEN_KEY);
const setStoredToken = (token, rememberMe) => {
  if (rememberMe) {
    localStorage.setItem(TOKEN_KEY, token);
    sessionStorage.removeItem(TOKEN_KEY);
  } else {
    sessionStorage.setItem(TOKEN_KEY, token);
    localStorage.removeItem(TOKEN_KEY);
  }
};
const clearStoredToken = () => {
  localStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(TOKEN_KEY);
};

userApi.interceptors.request.use((cfg) => {
  const t = getStoredToken();
  if (t) {
    cfg.headers = cfg.headers || {};
    cfg.headers.Authorization = `Bearer ${t}`;
  }
  return cfg;
});

export const formatAuthError = (err) => {
  const d = err?.response?.data?.detail;
  if (!d) return err?.message || "Errore sconosciuto";
  if (typeof d === "string") return d;
  if (Array.isArray(d))
    return d.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" · ");
  return JSON.stringify(d);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = loading, false = anon, object = logged
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const t = getStoredToken();
    if (!t) {
      setUser(false);
      return { user: false };
    }
    try {
      const { data } = await userApi.get("/auth/me");
      setUser(data);
      return { user: data };
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401 || status === 403) {
        // Token genuinely invalid/expired — this IS a logout.
        clearStoredToken();
        setUser(false);
        return { user: false };
      }
      // Transient failure (network blip, 5xx, 429 rate limit): never
      // downgrade an existing session to "guest" just because one check
      // failed — keep the token and whatever user state we already had.
      // `user: undefined` tells callers "inconclusive, don't act on this".
      return { user: undefined };
    }
  }, []);

  // Bootstrap check (once, on app load). `loading` only clears once we reach
  // a CONCLUSIVE answer: a transient failure on the very first check must not
  // make every logged-in route render as guest while a valid token is still
  // stored, so it gets one short retry before giving up.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { user: first } = await refresh();
      if (cancelled) return;
      if (first !== undefined) { setLoading(false); return; }
      setTimeout(async () => {
        if (cancelled) return;
        await refresh();
        if (!cancelled) setLoading(false);
      }, 1500);
    })();
    return () => { cancelled = true; };
  }, [refresh]);

  const login = async (email, password, rememberMe = true) => {
    try {
      const { data } = await userApi.post("/auth/login", { email, password, remember_me: !!rememberMe });
      setStoredToken(data.token, rememberMe);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatAuthError(e) };
    }
  };

  const register = async (email, password, name, consents = {}) => {
    try {
      const { data } = await userApi.post("/auth/register", {
        email,
        password,
        name,
        privacy_accepted: !!consents.privacy_accepted,
        marketing_authorized: !!consents.marketing_authorized,
      });
      setStoredToken(data.token, true);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatAuthError(e) };
    }
  };

  const logout = () => {
    clearStoredToken();
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh, userApi, getToken: getStoredToken }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
};
